import PropTypes from 'prop-types';
import { memo, useState, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import sanitizeHtml from 'sanitize-html';

import { Badge, Button, Modal, Text, useTheme } from '@Sage/hoi-poi-ui';
import { BillboardActions } from 'actions';
import { UserChip } from 'containers/components/chips';
import Scrollbars from 'components/ScrollBar';
import FileList from 'containers/components/FileList';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { successToast } from 'utils/toast';
import './styles.scss';

const propTypes = {
    model: PropTypes.object.isRequired,
};

const PanelReader = memo(({ model }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const loggedUser = useSelector((state) => state.config.userData.idUsuario);

    const infoRef = useRef(null);
    const actionsRef = useRef(null);
    const [contentOffsetHeight, setContentOffsetHeight] = useState(0);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);

    useLayoutEffect(() => {
        const observer = new ResizeObserver(() => {
            const infoH = infoRef.current ? infoRef.current.offsetHeight : 0;
            const actionsH = actionsRef.current ? actionsRef.current.offsetHeight : 0;
            setContentOffsetHeight(infoH + actionsH);
        });
        if (infoRef.current) observer.observe(infoRef.current);
        if (actionsRef.current) observer.observe(actionsRef.current);
        return () => observer.disconnect();
    }, []);

    const confirmMessage = useMemo(
        () => getLiteralWithParameters('label_billboard_modal_confirm', [model.username || '-']),
        [model.username],
    );

    const literalForConfirm = useMemo(
        () => getLiteralWithParameters('label_billboard_confirm_meaning', [model.username || '-']),
        [model.username],
    );

    const literalForConfirmed = useMemo(() => {
        const confirmedAt = model.getTimeConfirmed?.(loggedUser);
        return confirmedAt
            ? getLiteralWithParameters('label_billboard_confirmed_date', [confirmedAt])
            : getLiteral('label_billboard_confirmed_date');
    }, [loggedUser, model]);

    const handleConfirmBillboard = useCallback(() => {
        if (isConfirming) return;

        setIsConfirming(true);
        dispatch(
            BillboardActions.billboardActionConfirmed(model, () => {
                setIsConfirming(false);
                setConfirmModalOpen(false);
                successToast({ text: getLiteral('label_billboard_notif_confirmed') });
            }),
        );
    }, [dispatch, isConfirming, model]);

    const handleOpenConfirmModal = useCallback(() => {
        if (isConfirming) return;
        setConfirmModalOpen(true);
    }, [isConfirming]);

    const handleCloseConfirmModal = useCallback(() => {
        if (isConfirming) return;
        setConfirmModalOpen(false);
    }, [isConfirming]);

    const readerStateLiteral = model.userHasConfirmed?.(loggedUser)
        ? 'label_billboard_status_confirmed'
        : 'label_billboard_status_pending';
    const files = model.attachmentFiles;

    return (
        <div className="billboard-panel-reader">
            <div className="reader-info" ref={infoRef}>
                <div className="reader-main">
                    <div className="reader-from-box">
                        <Text
                            type="caption"
                            className="info-label"
                            color={theme.colors.utility.textSecondary}
                        >
                            {getLiteral('label_from')}
                        </Text>
                        <UserChip
                            idUser={model.idUser}
                            username={model.username}
                            remotePopover={true}
                        />
                    </div>
                    <Text
                        type="h6"
                        bold
                        as="div"
                        className="reader-title"
                        color={theme.colors.utility.textPrimary}
                    >
                        {model.subject}
                    </Text>
                    <Text
                        type="body"
                        as="div"
                        className="reader-startdate"
                        color={theme.colors.utility.textSecondary}
                    >
                        {model.startDateNotification}
                    </Text>
                    <Badge type="semanticInfo" variant="inverted" className="reader-state">
                        {getLiteral(readerStateLiteral)}
                    </Badge>
                </div>
            </div>
            <div
                className="reader-content"
                style={{ height: `calc(100% - ${contentOffsetHeight}px)` }}
            >
                <Scrollbars autoHide={true}>
                    <FileList files={files} />
                    <div
                        className="ql-editor reader-description"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(model.description) }}
                    />
                </Scrollbars>
            </div>
            <div className="reader-info-actions" ref={actionsRef}>
                {!model?.userHasConfirmed?.(loggedUser) ? (
                    <div className="reader-confirm">
                        <Text
                            type="body"
                            className="reader-confirm-literal"
                            color={theme.colors.utility.textSecondary}
                        >
                            {literalForConfirm}
                        </Text>
                        <Button
                            className="reader-confirm-button"
                            onClick={handleOpenConfirmModal}
                            size="big"
                            type="primary"
                        >
                            {getLiteral('action_billboard_confirm')}
                        </Button>
                    </div>
                ) : (
                    <Text
                        type="body"
                        as="div"
                        className="time-confirmed-text"
                        color={theme.colors.utility.textSecondary}
                    >
                        {literalForConfirmed}
                    </Text>
                )}
            </div>

            <Modal
                cancelText={getLiteral('common_no')}
                confirmText={getLiteral('common_yes')}
                isConfirmDisabled={isConfirming}
                isConfirmLoading={isConfirming}
                isOpen={confirmModalOpen}
                onCancel={handleCloseConfirmModal}
                onConfirm={handleConfirmBillboard}
                onRequestClose={handleCloseConfirmModal}
                size="small"
                title={getLiteral('title_warning')}
            >
                <Text type="body">{confirmMessage}</Text>
            </Modal>
        </div>
    );
});

PanelReader.propTypes = propTypes;

export default PanelReader;
